// Dashboard teaser for the daily game-wins leaderboard: top 5, the signed-in
// player's position, and a CTA to the full Leaderboard page.
// Design source: claude.ai design project "Circlechell leaderboard dashboard".

import React, {useRef, useState} from 'react';
import {
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import CCText from '@/Atoms/CCText';
import Reveal from '@/Atoms/Reveal';
import Shine from '@/Atoms/Shine';
import NudgeArrow from '@/Atoms/NudgeArrow';
import {hoverLift, transition} from '@/Utils/motion';
import {ScreenName} from '@/navigators/StackConstants';
import {logEvent} from '@/Utils/GAEvents';
import {
  WIDE_BREAKPOINT,
  lbColors,
  lbRadius,
  lbShadow,
} from '@/Screens/Leaderboard/leaderboardTheme';
import {useLeaderboardData} from '@/Screens/Leaderboard/useLeaderboardData';
import LiveBadge from '@/Screens/Leaderboard/components/LiveBadge';
import ScrollHint from './ScrollHint';
import {dsIcons, MEDAL_BY_RANK} from '../../designIcons';
import PlayerAvatar from '@/Screens/Leaderboard/components/PlayerAvatar';
import {
  SkeletonChip,
  SkeletonRows,
} from '@/Screens/Leaderboard/components/Skeleton';
import {radii} from '@/Utils/radius';

const PREVIEW_LIMIT = 5;

function LeaderRow({r, index = 0}: {r: any; index?: number}) {
  return (
    // Staggered entrance: the board deals itself in rather than appearing
    // whole, which is what makes a leaderboard feel live.
    <Reveal delay={index * 55} distance={10}>
      <Pressable
        // Hover only — the rows aren't navigable, so keep the default cursor
        // rather than promising a click that doesn't exist.
        style={({hovered}: any) => [
          styles.row,
          {
            cursor: 'default',
            backgroundColor: r.flash
              ? lbColors.flashBg
              : r.you
              ? lbColors.youRowBg
              : lbColors.rowBg,
          },
          hovered && styles.rowHovered,
        ]}>
      {/* Design renders a medal svg for the top 3, a plain rank below that. */}
      {MEDAL_BY_RANK[r.rank] ? (
        <Image
          source={MEDAL_BY_RANK[r.rank]}
          style={styles.medal}
          resizeMode="contain"
        />
      ) : (
        <CCText
          fontSize={13.5}
          fontWeight="bold"
          color={lbColors.muted}
          style={styles.rank}>
          {r.rank}
        </CCText>
      )}
      <PlayerAvatar
        initials={r.initials}
        color={r.color}
        avatarUrl={r.avatarUrl}
        size={32}
      />
      <CCText
        fontSize={15}
        fontWeight="bold"
        color={lbColors.ink}
        showEllipses
        style={styles.name}>
        {r.name}
      </CCText>
      <View style={styles.winsWrap}>
        <CCText fontSize={18} fontWeight="bold" color={lbColors.ink}>
          {r.wins}
        </CCText>
        <CCText fontSize={12} fontWeight="semibold" color={lbColors.muted}>
          {' wins'}
        </CCText>
      </View>
      </Pressable>
    </Reveal>
  );
}

interface LeaderboardPreviewProps {
  // Pinned rail placement (e.g. beside the hero/trainer column on desktop) is
  // narrow regardless of screen width, so it always wants the stacked
  // (non-`wide`) body layout rather than the side-by-side one.
  compact?: boolean;
}

function LeaderboardPreview({compact}: LeaderboardPreviewProps) {
  const navigation = useNavigation();
  // Drives the "more below" hint: true once the rows overflow the capped rail
  // and the user hasn't scrolled yet.
  const [hasOverflow, setHasOverflow] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const railScrollRef = useRef<any>(null);
  const [contentH, setContentH] = useState(0);
  const [viewportH, setViewportH] = useState(0);
  // onScroll only fires once the user moves, so measure directly for the
  // initial "is there more?" decision.
  const overflowing = hasOverflow || contentH - viewportH > 8;
  const {width} = useWindowDimensions();
  const wide = !compact && width >= WIDE_BREAKPOINT;
  const {rows, me, totalPlayers, isLoading, isError} = useLeaderboardData(
    'daily',
    {refetchIntervalMs: 60000},
  );
  const top = rows.slice(0, PREVIEW_LIMIT);

  const onViewAll = () => {
    logEvent('event', 'leaderboard_preview_view_all', 'dashboard');
    navigation.navigate(ScreenName.Leaderboard);
  };

  // Hide a broken teaser on error, or once loaded with no players — but while
  // loading, keep the card mounted and show skeletons so it doesn't pop in
  // after the rest of the dashboard.
  if (isError || (!isLoading && top.length === 0)) {
    return null;
  }

  return (
    <View style={[styles.card, compact && styles.cardCompact, lbShadow.card]}>
      {/* header */}
      <View style={styles.header}>
        <View style={styles.iconCircle}>
          <Image
            source={dsIcons.tournament}
            style={{width: compact ? 20 : 24, height: compact ? 20 : 24}}
            resizeMode="contain"
          />
        </View>
        <View style={{flex: 1}}>
          <View style={styles.titleRow}>
            <CCText
              fontSize={compact ? 17 : 21}
              fontWeight="bold"
              color={lbColors.ink}
              showEllipses
              style={{flexShrink: 1, minWidth: 0}}>
              Daily Leaderboard
            </CCText>
            <LiveBadge />
          </View>
          {compact ? (
            <Pressable onPress={onViewAll} style={styles.fullLeaderboardCompact}>
              <CCText
                fontSize={12.5}
                fontWeight="bold"
                color={lbColors.goldInk}
                showEllipses
                style={{flexShrink: 1}}>
                Full leaderboard ›
              </CCText>
            </Pressable>
          ) : null}
          <CCText
            fontSize={13.5}
            fontWeight="semibold"
            color={lbColors.muted}
            style={{marginTop: 2}}>
            Top players by wins today · updates in real time
          </CCText>
        </View>
        {wide ? <ViewAllButton onPress={onViewAll} /> : null}
      </View>

      {/* Rail: fixed PLAYER/WINS header above the scrolling rows. */}
      {compact && !isLoading && top.length > 0 ? (
        <View style={styles.colHeaderRow}>
          <CCText
            fontSize={11}
            fontWeight="bold"
            color={lbColors.muted}
            style={styles.colHeaderPlayer}>
            PLAYER
          </CCText>
          <CCText fontSize={11} fontWeight="bold" color={lbColors.muted}>
            WINS
          </CCText>
        </View>
      ) : null}

      <View style={[styles.body, wide && styles.bodyWide, compact && styles.bodyCompact]}>
        {/* top list */}
        <View
          style={[
            styles.topList,
            wide && {flex: 1.6},
            compact && styles.topListCompact,
          ]}>
          {/* In the rail the column header is fixed above the scroll area
              (design boardBodyStyle scrolls the rows only), so it's rendered
              outside this block for `compact`. */}
          {!compact && !isLoading && top.length > 0 ? (
            <View style={styles.colHeaderRow}>
              <CCText fontSize={11} fontWeight="bold" color={lbColors.muted} style={styles.colHeaderPlayer}>
                PLAYER
              </CCText>
              <CCText fontSize={11} fontWeight="bold" color={lbColors.muted}>
                WINS
              </CCText>
            </View>
          ) : null}
          {isLoading ? (
            <SkeletonRows count={PREVIEW_LIMIT} compact />
          ) : compact ? (
            // ScrollView (not overflow:scroll) so the browser scrollbars stay
            // hidden while the rows still scroll inside the capped card.
            <View style={styles.railScrollWrap}>
              <ScrollView
                ref={railScrollRef}
                style={styles.railScroll}
                contentContainerStyle={styles.railScrollContent}
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                scrollEventThrottle={16}
                onScroll={e => {
                  const {contentOffset, contentSize, layoutMeasurement} =
                    e.nativeEvent;
                  setScrolled(contentOffset.y > 4);
                  setHasOverflow(
                    contentSize.height - layoutMeasurement.height > 8,
                  );
                }}
                onContentSizeChange={(_w, h) => setContentH(h)}
                onLayout={e => setViewportH(e.nativeEvent.layout.height)}>
                {top.map((r, i) => (
                  <LeaderRow key={r.userid} r={r} index={i} />
                ))}
              </ScrollView>
              <ScrollHint
                visible={overflowing && !scrolled}
                onPress={() =>
                  railScrollRef.current?.scrollTo({
                    y: Math.max(0, contentH - viewportH),
                    animated: true,
                  })
                }
              />
            </View>
          ) : (
            top.map((r, i) => <LeaderRow key={r.userid} r={r} index={i} />)
          )}
        </View>

        {/* your position */}
        {isLoading ? (
          <View style={wide ? {flex: 1} : undefined}>
            <SkeletonChip />
          </View>
        ) : !me || me.rank == null ? (
          // No position yet — either the user isn't on today's board at all, or
          // they are but unranked. A bare "—" told them nothing and offered no
          // way out; the only thing that earns a rank is playing, so say that
          // and give them the button.
          <View
            style={[
              styles.youChip,
              wide && {flex: 1},
              compact && styles.youChipCompact,
            ]}>
            <CCText
              fontSize={11.5}
              fontWeight="bold"
              color={lbColors.youChipLabel}
              style={styles.youLabelTight}>
              YOUR POSITION TODAY
            </CCText>
            <View style={styles.emptyBody}>
              <View style={styles.emptyCopy}>
                <CCText fontSize={13} fontWeight="bold" color={lbColors.ink}>
                  Not on today's board yet
                </CCText>
                <CCText
                  fontSize={11.5}
                  fontWeight="semibold"
                  color={lbColors.youChipSub}
                  style={{marginTop: 2}}>
                  Win a game to claim a rank
                </CCText>
              </View>
              <Pressable
                onPress={() => {
                  logEvent('event', 'leaderboard_preview_play', 'dashboard');
                  navigation.navigate(ScreenName.PlayChess);
                }}
                style={({hovered, pressed}: any) => [
                  styles.emptyCta,
                  hovered && styles.ctaHovered,
                  pressed && {opacity: 0.85},
                ]}>
                {({hovered}: any) => (
                  <>
                    <CCText
                      fontSize={12.5}
                      fontWeight="bold"
                      color={lbColors.goldInk}>
                      Play a game
                    </CCText>
                    <NudgeArrow
                      hovered={hovered}
                      glyph="→"
                      fontSize={14}
                      color={lbColors.goldInk}
                    />
                  </>
                )}
              </Pressable>
            </View>
          </View>
        ) : (
          <View
            style={[
              styles.youChip,
              wide && {flex: 1},
              compact && styles.youChipCompact,
            ]}>
            <CCText
              fontSize={12}
              fontWeight="bold"
              color={lbColors.youChipLabel}
              style={styles.youLabel}>
              YOUR POSITION TODAY
            </CCText>
            <View style={styles.youBody}>
              <PlayerAvatar
                initials={me.initials}
                color={lbColors.teal}
                avatarUrl={me.avatarUrl}
                size={compact ? 36 : 52}
              />
              <View style={{flex: 1}}>
                <CCText fontSize={30} fontWeight="bold" color={lbColors.ink}>
                  {`#${me.rank}`}
                </CCText>
                <CCText
                  fontSize={13}
                  fontWeight="semibold"
                  color={lbColors.youChipSub}
                  style={{marginTop: 3}}>
                  of {totalPlayers} players
                </CCText>
              </View>
              <View style={{alignItems: 'flex-end'}}>
                <CCText fontSize={26} fontWeight="bold" color={lbColors.goldInk}>
                  {me.wins}
                </CCText>
                <CCText
                  fontSize={12}
                  fontWeight="semibold"
                  color={lbColors.youChipLabel}>
                  wins today
                </CCText>
              </View>
            </View>
          </View>
        )}
      </View>

      {/* The rail already has "All ›" in its header (design shows no bottom
          CTA there), so only the standalone card gets the full-width button. */}
      {!wide && !compact ? <ViewAllButton onPress={onViewAll} full /> : null}
    </View>
  );
}

function ViewAllButton({onPress, full}: {onPress: () => void; full?: boolean}) {
  return (
    <Pressable
      onPress={onPress}
      style={({hovered, pressed}: any) => [
        styles.cta,
        full && styles.ctaFull,
        hovered && styles.ctaHovered,
        pressed && {opacity: 0.85},
      ]}>
      {({hovered}: any) => (
        <>
          {/* Offset from the hero's sweep so the two gold CTAs on screen don't
              flash in lockstep. */}
          <Shine travel={230} width={40} interval={3400} delay={900} />
          <CCText fontSize={14} fontWeight="bold" color={lbColors.goldInk}>
            View full leaderboard
          </CCText>
          <NudgeArrow
            hovered={hovered}
            glyph="→"
            fontSize={16}
            color={lbColors.goldInk}
          />
        </>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: lbColors.card,
    borderRadius: radii.panel,
    padding: 26,
    // 'auto' + stretch: a 100% basis on top of the card's margins overflowed
    // the rail to the right.
    flexBasis: 'auto',
    alignSelf: 'stretch',
    minWidth: 0,
    marginVertical: 8,
  },
  // Pinned-rail placement: fill the band's height (design boardCardStyle
  // `flex:1`) rather than sizing to content, and tighten the padding for the
  // narrow column.
  cardCompact: {
    flex: 1,
    flexBasis: 'auto',
    // Vertical only: the rail COLUMN carries the 8px horizontal inset, so a
    // horizontal margin here would push this card 8px further in than the
    // sections beside it.
    marginVertical: 8,
    // Card is height-capped by the band; the rows list is what gives, so the
    // footer chip stays pinned and visible (design boardCardStyle).
    minHeight: 0,
    overflow: 'hidden',
    padding: 18,
  },
  bodyCompact: {
    flex: 1,
    minHeight: 0,
    gap: 12,
  },
  topListCompact: {
    flex: 1,
    minHeight: 0,
    // Scrolling is handled by the inner ScrollView (which keeps its
    // scrollbars hidden), so this must not create a second scroll container.
    overflow: 'hidden',
  },
  railScrollWrap: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
  },
  railScroll: {
    flex: 1,
    minHeight: 0,
  },
  railScrollContent: {
    gap: 5,
  },
  youChipCompact: {
    flexShrink: 0,
    padding: 14,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    marginBottom: 20,
    flexWrap: 'wrap',
    flexShrink: 0,
  },
  iconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: lbColors.orange,
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    rowGap: 4,
  },
  fullLeaderboardCompact: {
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  body: {gap: 20},
  bodyWide: {flexDirection: 'row'},
  topList: {gap: 5},
  colHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingBottom: 8,
    marginBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(38,35,34,0.10)',
    flexShrink: 0,
  },
  colHeaderPlayer: {marginLeft: 44},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: radii.chip,
    ...transition(150),
  },
  rowHovered: {
    backgroundColor: '#FFF6DC',
    transform: [{translateX: 3}],
  },
  rank: {width: 22, textAlign: 'center'},
  medal: {width: 21, height: 21, flexShrink: 0},
  name: {flex: 1},
  winsWrap: {flexDirection: 'row', alignItems: 'baseline'},
  youChip: {
    backgroundColor: lbColors.youChipBg,
    borderRadius: radii.chip,
    padding: 22,
    justifyContent: 'center',
  },
  youLabel: {letterSpacing: 0.5, marginBottom: 14},
  youBody: {flexDirection: 'row', alignItems: 'center', gap: 14},
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: lbColors.gold,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: lbRadius.pill,
    position: 'relative',
    overflow: 'hidden',
    ...transition(180),
  },
  ctaHovered: {
    ...hoverLift(2),
    shadowColor: '#B98F14',
    shadowOpacity: 0.4,
  },
  ctaFull: {marginTop: 14, justifyContent: 'center'},
  // The empty state carries three short lines and a button, where the ranked
  // state carries an avatar and 30px numerals. Reusing the ranked chip's 22px
  // padding + 14px label gap made it taller than the two leaderboard rows it
  // was pushing out of view.
  youLabelTight: {letterSpacing: 0.5, marginBottom: 6},
  // Copy left, button right: centred and stacked, this block was as tall as
  // two leaderboard rows for two short lines and a pill.
  emptyBody: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
    paddingVertical: 2,
  },
  emptyCopy: {
    flex: 1,
    minWidth: 132,
  },
  emptyCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    flexShrink: 0,
    backgroundColor: lbColors.gold,
    paddingVertical: 7,
    paddingHorizontal: 15,
    borderRadius: lbRadius.pill,
    cursor: 'pointer',
    ...transition(180),
  },
});

export default LeaderboardPreview;
